import { Eye, EyeOff } from "lucide-react";
import type { JSX } from "react";
import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";

import { supabase, useSession } from "@/lib/supabase";

interface LocationState {
  from?: string;
}

function safeInternalPath(from: string | undefined): string {
  if (!from || !from.startsWith("/") || from.startsWith("//")) return "/";
  return from;
}

export default function LoginPage(): JSX.Element {
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const state = location.state as LocationState | null;
  const redirectTo = safeInternalPath(state?.from);

  if (session.status === "authenticated") {
    return <Navigate to={redirectTo} replace />;
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (authError) {
      setError(authError.message);
      return;
    }
    navigate(redirectTo, { replace: true });
  }

  return (
    <main className="dark relative min-h-screen overflow-hidden bg-background flex items-center justify-center p-6">
      {/* Decorative blobs */}
      <img
        src="/login-top-figure.svg"
        alt=""
        className="absolute top-0 left-0 pointer-events-none select-none"
      />
      <img
        src="/login-bottom-figure.svg"
        alt=""
        className="absolute bottom-0 right-0 pointer-events-none select-none"
      />

      <div className="relative z-10 w-full max-w-sm space-y-6">
        {/* Logo */}
        <div className="flex flex-col items-center gap-1">
          <img src="/beflow-logo-white-slogan.svg" alt="BeFlow" className="h-20" />
        </div>

        {/* Heading */}
        <div className="text-center space-y-2">
          <h1 className="text-heading-1 font-bold text-foreground">Bienvenido/a</h1>
          <p className="text-subtitle text-foreground">Graph RAG</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            id="email"
            type="email"
            aria-label="Correo electrónico"
            placeholder="Correo electrónico"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            className="w-full h-12 rounded-lg bg-muted border-0 px-4 text-body-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              aria-label="Contraseña"
              placeholder="Contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full h-12 rounded-lg bg-muted border-0 px-4 pr-12 text-body-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {error && (
            <p role="alert" className="text-body-3 text-destructive">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="mt-2 w-full h-12 rounded-lg bg-[#f27127] text-white text-body-2 font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {submitting ? "Iniciando sesión…" : "Iniciar sesión"}
          </button>

          <Link
            to="/forgot-password"
            className="block text-center text-body-3 text-muted-foreground hover:text-foreground underline"
          >
            ¿Olvidaste tu contraseña?
          </Link>
        </form>
      </div>
    </main>
  );
}
